import { useNavigate, useRouterState } from '@tanstack/react-router';
import { type ReactNode, useEffect } from 'react';

import { useAuth } from './auth-context';
import { currentReturnTo } from './return-to';
import { CS_HOME, canAccessPath } from './roles';

/**
 * Gates a page behind a live session; bounces to /login once bootstrap settles,
 * carrying where to come back to. The MCP approval page depends on that round trip:
 * its request id lives in the URL and the pending request expires in ten minutes, so
 * landing back on `/` would strand the connection.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated, isBootstrapping, user } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => (s.resolvedLocation ?? s.location).pathname });
  const forbidden = user !== null && !canAccessPath(user.role, pathname);

  useEffect(() => {
    if (!isBootstrapping && !isAuthenticated) {
      void navigate({ to: '/login', search: { redirect: currentReturnTo() } });
    }
  }, [isBootstrapping, isAuthenticated, navigate]);

  useEffect(() => {
    if (forbidden) {
      void navigate({ to: CS_HOME, replace: true });
    }
  }, [forbidden, navigate]);

  if (isBootstrapping) {
    return (
      <main className="grid min-h-screen place-items-center">
        <p className="text-sm text-[var(--color-muted-foreground)]">Restoring session…</p>
      </main>
    );
  }

  if (!isAuthenticated || forbidden) {
    return null;
  }

  return children;
}
